import { User as UserIcon } from "lucide-react";
import type { OrganizationType } from "@bookdental/schemas";
import { cn } from "@/lib/cn";

// Cores sólidas (sem degradê: o degradê é só do avatar de paciente). A cor sai do id, não do nome:
// duas organizações com a mesma inicial ainda ficam diferentes.
const CORES = [
  { fundo: "#DBEAFE", texto: "#1D4ED8" },
  { fundo: "#D1FAE5", texto: "#047857" },
  { fundo: "#FEF3C7", texto: "#B45309" },
  { fundo: "#FCE7F3", texto: "#BE185D" },
  { fundo: "#EDE9FE", texto: "#6D28D9" },
  { fundo: "#CCFBF1", texto: "#0F766E" },
  { fundo: "#FFE4E6", texto: "#BE123C" },
  { fundo: "#E0E7FF", texto: "#4338CA" },
];
const LIGACOES = new Set(["de", "da", "do", "das", "dos", "e"]);

function corDe(id: string) {
  let h = 0;
  for (const c of id) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return CORES[h % CORES.length]!;
}

export function iniciaisDe(nome: string) {
  const palavras = nome.trim().split(/\s+/).filter((p) => p && !LIGACOES.has(p.toLowerCase()));
  if (palavras.length >= 2) return (palavras[0]![0]! + palavras[1]![0]!).toUpperCase();
  return (palavras[0] ?? "?").slice(0, 2).toUpperCase();
}

/** "Laboratório", "Clínica", "Clínica e laboratório", "Radiologia" — o que evita agir na errada. */
export function tipoDaOrganizacao(types: OrganizationType[]) {
  const lab = types.includes("DENTAL_LAB");
  const clinica = types.includes("DENTAL_OFFICE");
  if (lab && clinica) return "Clínica e laboratório";
  if (lab) return "Laboratório";
  if (types.includes("RADIOLOGY_CENTER")) return "Radiologia";
  return "Clínica";
}

/** A cara da organização no switcher: o logo dela, senão as iniciais na cor dela. */
export function AvatarDaOrganizacao({
  id,
  nome,
  logo,
  pessoal,
  className,
}: {
  id: string;
  nome: string;
  logo?: string | null;
  pessoal: boolean;
  className?: string;
}) {
  const base = cn("flex shrink-0 items-center justify-center overflow-hidden rounded-md", className);
  if (pessoal) {
    return (
      <span className={cn(base, "bg-zinc-100 text-zinc-600 dark:bg-muted dark:text-foreground/80")}>
        <UserIcon className="h-4 w-4" />
      </span>
    );
  }
  if (logo) {
    return (
      <span className={cn(base, "border border-zinc-200 bg-white dark:border-border")}>
        <img src={logo} alt="" className="h-full w-full object-contain p-0.5" />
      </span>
    );
  }
  const cor = corDe(id);
  return (
    <span className={cn(base, "text-[11px] font-bold tracking-wide")} style={{ backgroundColor: cor.fundo, color: cor.texto }} aria-hidden>
      {iniciaisDe(nome)}
    </span>
  );
}
